import {useCallback, useMemo, useRef, useState} from 'react';
import {Autoplay} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Swiper as SwiperClass} from 'swiper/types';
import {getValidChildren} from '../../../common/shared/utils/getValidChildren';
import {twMerge} from '../../../common/twMerge';
import {BigBannerMobileSwiperCarouselProps} from './BigBannerMobileCarouselProps.interface';

/**
 * Carousel과 동작은 동일하지만, 모바일 빅배너에서만 양쪽 간격이 아니라, 한쪽 간격만 사용하여 중앙정렬을 하기 위한 컴포넌트입니다.
 */
export function BigBannerMobileSwiperCarousel({
  isInfinite,
  autoInterval,
  isAutoPlay,
  onChangeIndex,
  renderDots,
  renderPrevArrow,
  renderNextArrow,
  onSwiper,
  children,
  className,
  gapPx,
  responsive: _,
  ...carouselProps
}: BigBannerMobileSwiperCarouselProps) {
  const swiperRef = useRef<SwiperClass>();

  const validChildren = useMemo(() => getValidChildren(children), [children]);

  const onClickNextArrow = useCallback(() => {
    swiperRef.current?.slideNext();
  }, []);

  const onClickPrevArrow = useCallback(() => {
    swiperRef.current?.slidePrev();
  }, []);

  const [currentIndex, setCurrentIndex] = useState<number>(1);

  if (!validChildren || !validChildren.length) {
    return null;
  }

  return (
    <div className={twMerge(className)} {...carouselProps}>
      <Swiper
        modules={[Autoplay]}
        className={'h-full w-full touch-pan-y select-none'}
        observer
        threshold={20}
        loop={isInfinite}
        navigation={true}
        autoplay={
          isAutoPlay && validChildren.length > 1
            ? {delay: autoInterval}
            : undefined
        }
        slidesPerView="auto"
        allowSlidePrev={true}
        onSwiper={swiper => {
          swiperRef.current = swiper;
          onSwiper?.(swiper);
        }}
        // mouse 이벤트를 활성화하시 위해 false로 세팅
        touchStartPreventDefault={false}
        onSlideChange={swiper => {
          setCurrentIndex(swiper.realIndex);
          onChangeIndex?.(swiper.realIndex);
        }}
        slidesOffsetBefore={gapPx}
        slidesOffsetAfter={gapPx}
        spaceBetween={gapPx}
      >
        {validChildren.map(
          (slide, index) =>
            slide && (
              <SwiperSlide
                key={index}
                dir="ltr"
                style={
                  gapPx
                    ? {
                        width: `calc(100% - ${gapPx * 2}px)`,
                      }
                    : undefined
                }
              >
                <div className={twMerge('w-full object-contain')}>
                  <div className="relative block pb-[100%]">
                    <div className="absolute h-full w-full">{slide}</div>
                  </div>
                </div>
              </SwiperSlide>
            )
        )}
        {renderNextArrow?.(onClickNextArrow)}
        {renderPrevArrow?.(onClickPrevArrow)}
        {validChildren.length > 1 &&
          renderDots?.(currentIndex, validChildren.length)}
      </Swiper>
    </div>
  );
}
